import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductTabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/**
 * A simple ARIA tablist that switches between product info panels
 * (e.g. Details & Description / Washcare / Shipping).
 *
 * The component element itself is the tablist container — click/keydown
 * delegation and the `role="tablist"` live on it directly, since each tab
 * button and panel are rendered together by the same `_product-tab-panel`
 * child block (see `_product-tabs.liquid` for why) rather than under a
 * separate dedicated tablist wrapper.
 *
 * @extends {Component<ProductTabsRefs>}
 */
export class ProductTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];
  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const selectedIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#active = selectedIndex >= 0 ? selectedIndex : 0;
    this.#update();
  }

  /** @param {MouseEvent} event */
  handleTabClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[role="tab"]');
    if (!tab) return;

    const index = this.refs.tabs.indexOf(/** @type {HTMLElement} */ (tab));
    if (index === -1) return;

    this.#activate(index);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[role="tab"]');
    if (!tab) return;

    const { tabs } = this.refs;
    const index = tabs.indexOf(/** @type {HTMLElement} */ (tab));
    if (index === -1) return;

    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: tabs.length - 1 - index,
    };

    const offset = navMap[/** @type {keyof typeof navMap} */ (event.key)];
    if (offset === undefined) return;

    event.preventDefault();
    const nextIndex = (index + offset + tabs.length) % tabs.length;
    this.#activate(nextIndex);
    tabs[nextIndex]?.focus();
  }

  /** @param {number} index */
  #activate(index) {
    if (index === this.#active || index < 0 || index >= this.refs.tabs.length) return;

    this.#active = index;
    this.#update();
  }

  #update() {
    const { tabs, panels } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === this.#active;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.toggleAttribute('hidden', i !== this.#active);
    }
  }
}

customElements.define('product-tabs-component', ProductTabsComponent);
